import { useForm } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Check, MailCheck } from "lucide-react"

function RegistrationForm({ coach }) {
  const form = useForm({
    name: "",
    email: "",
    website_url: "", // honeypot
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    form.post(`/plan/${coach.slug}`)
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Honeypot — hidden from real users, bots auto-fill it */}
      <div className="absolute overflow-hidden" style={{ width: 0, height: 0, opacity: 0 }} aria-hidden="true" tabIndex={-1}>
        <label htmlFor="website_url">Website URL</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={form.data.website_url}
          onChange={(e) => form.setData("website_url", e.target.value)}
          autoComplete="off"
          tabIndex={-1}
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div className="space-y-2">
          <Label htmlFor="name">Your name</Label>
          <Input
            id="name"
            name="name"
            value={form.data.name}
            onChange={(e) => form.setData("name", e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={form.data.email}
            onChange={(e) => form.setData("email", e.target.value)}
            required
          />
        </div>
      </div>

      <Button type="submit" variant="gradient" disabled={form.processing}>
        Start my assessment
      </Button>
    </form>
  )
}

export default function PlanWelcome({ seo, coach, settings = {}, allowRegistration = false, justRegistered = false }) {
  const { scheduleCallUrl = "#" } = settings

  return (
    <>
      <SeoHead seo={seo} />
      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="plan" scheduleCallUrl={scheduleCallUrl} />

        <section className="pt-32 pb-16">
          <div className="mx-auto max-w-3xl px-6">
            <p className="text-sm uppercase tracking-wider text-kf-red font-semibold mb-4">
              Invited by {coach.name}
            </p>
            <h1 className="font-display text-5xl md:text-6xl leading-tight mb-6">
              A personalized business + spiritual game plan
            </h1>
            <p className="text-lg leading-relaxed text-zinc-700 mb-10">
              The Kingdom Factor assessment is the same framework we use with
              current Kingdom Factor Members. In about 15–20 minutes, you'll
              complete an Integrated Life Audit across six dimensions and answer
              ten questions about where you are right now — and you'll receive a
              personalized report you can take to your next conversation with{" "}
              {coach.name}.
            </p>

            <div className="rounded-lg border border-zinc-200 p-6 bg-zinc-50 mb-10">
              <h2 className="font-display text-xl mb-4">What you'll receive</h2>
              <ul className="space-y-2 text-zinc-700">
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-kf-red shrink-0 mt-0.5" />
                  <span>An Integrated Life Audit snapshot across six dimensions</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-kf-red shrink-0 mt-0.5" />
                  <span>Your scores read back to you area by area — faith, family, physical, financial, business, relationships</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-kf-red shrink-0 mt-0.5" />
                  <span>A personalized narrative report drawn from your own answers, not a generic template</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-kf-red shrink-0 mt-0.5" />
                  <span>Practical next steps to talk through with {coach.name}</span>
                </li>
              </ul>
            </div>

            {justRegistered ? (
              <div className="rounded-lg border border-zinc-200 p-6 bg-zinc-50 mb-10">
                <div className="flex items-start gap-3">
                  <MailCheck className="w-6 h-6 text-kf-red shrink-0 mt-0.5" />
                  <div>
                    <h2 className="font-display text-xl mb-2">Check your email</h2>
                    <p className="text-zinc-700">
                      We just sent a secure link to begin your assessment with{" "}
                      {coach.name}. No account or password needed — just click the
                      link in your inbox to get started. The link stays valid for
                      30 days, so you can pick up right where you left off.
                    </p>
                  </div>
                </div>
              </div>
            ) : allowRegistration ? (
              <div className="rounded-lg border border-zinc-200 p-6 mb-10">
                <h2 className="font-display text-xl mb-2">Get started</h2>
                <p className="text-zinc-700 mb-4">
                  Enter your name and email to begin your assessment with{" "}
                  {coach.name}. No password needed — we'll also email you a
                  secure link so you can pick it back up later or finish on
                  another device.
                </p>
                <RegistrationForm coach={coach} />
              </div>
            ) : (
              <div className="rounded-lg border border-zinc-200 p-6 mb-10">
                <h2 className="font-display text-xl mb-2">How to get started</h2>
                <p className="text-zinc-700">
                  Reach out to {coach.name}
                  {coach.city && coach.state ? ` (${coach.city}, ${coach.state})` : ""}
                  . They'll send you a secure invite email with a link to begin
                  your assessment.
                </p>
              </div>
            )}

            {coach.bio && (
              <div className="rounded-lg border border-zinc-200 p-6">
                <h2 className="font-display text-xl mb-3">About {coach.name}</h2>
                <p className="text-zinc-700 whitespace-pre-line">{coach.bio}</p>
              </div>
            )}
          </div>
        </section>

        <Footer activePage="plan" />
      </div>
    </>
  )
}
